/**
 * ReportCard Component
 * 
 * Displays individual report information with actions like download, view, delete
 * Matches backend ReportResponse schema and provides production-ready functionality
 */

import React, { useState } from 'react';
import { Card, Badge, Button, Dropdown, Spinner, ProgressBar } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { 
    BsFileText, 
    BsDownload, 
    BsEye, 
    BsTrash, 
    BsStopCircle,
    BsThreeDotsVertical,
    BsClock,
    BsCheckCircle,
    BsExclamationTriangle,
    BsCloudDownload
} from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import { deleteReport, fetchReportById } from '../../../redux/slices/reportingSlice';
import reportingService from '../../../services/reportingService';

const ReportCard = ({ 
    report, 
    onView, 
    onDownload, 
    onDelete,
    isSelected = false,
    onSelect,
    viewMode = 'grid' // 'grid' | 'list'
}) => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [showActions, setShowActions] = useState(false);

    // Status configuration
    const getStatusConfig = (status) => {
        switch (status) {
            case 'completed':
                return { variant: 'success', icon: BsCheckCircle, text: 'Completed' };
            case 'running':
                return { variant: 'primary', icon: BsClock, text: 'Running' };
            case 'pending':
                return { variant: 'warning', icon: BsClock, text: 'Pending' };
            case 'failed':
                return { variant: 'danger', icon: BsExclamationTriangle, text: 'Failed' };
            case 'cancelled':
                return { variant: 'secondary', icon: BsStopCircle, text: 'Cancelled' };
            default:
                return { variant: 'secondary', icon: BsClock, text: status };
        }
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (!bytes) return 'N/A';
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-GB');
    };

    // Get export format icon
    const getFormatIcon = (format) => {
        switch (format?.toLowerCase()) {
            case 'pdf':
                return '📄';
            case 'excel':
                return '📊';
            case 'csv':
                return '📈';
            case 'json':
                return '🗂️';
            default:
                return '📄';
        }
    };

    // Handle download
    const handleDownload = async () => {
        if (report.status !== 'completed') return;
        
        setIsLoading(true);
        try {
            const response = await reportingService.downloadReport(report.id);
            if (response.success) {
                // Create download link
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `report_${report.id}.${report.export_format?.toLowerCase() || 'pdf'}`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                
                if (onDownload) onDownload(report);
            }
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    // Handle view
    const handleView = () => {
        if (onView) {
            onView(report);
        } else {
            dispatch(fetchReportById(report.id));
        }
    };

    // Handle delete
    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this report?')) {
            try {
                await dispatch(deleteReport(report.id));
                if (onDelete) onDelete(report);
            } catch (error) {
            }
        }
    };

    // Handle cancel (for running reports)
    const handleCancel = async () => {
        if (window.confirm('Are you sure you want to cancel this report generation?')) {
            setIsLoading(true);
            try {
                await reportingService.cancelReport(report.id);
            } catch (error) {
            } finally {
                setIsLoading(false);
            }
        }
    };

    const statusConfig = getStatusConfig(report.status);
    const StatusIcon = statusConfig.icon;

    if (viewMode === 'list') {
        return (
            <Card className={`report-card-list border-0 shadow-sm mb-2 ${isSelected ? 'border-primary' : ''}`}>
                <Card.Body className="py-3">
                    <div className="row align-items-center">
                        <div className="col-md-1">
                            {onSelect && (
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => onSelect(report.id, e.target.checked)}
                                    className="form-check-input"
                                />
                            )}
                        </div>
                        <div className="col-md-1">
                            <div className="report-icon">
                                <BsFileText className="h4 text-primary mb-0" />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <h6 className="mb-1 fw-semibold">
                                {typeof report.template_id === 'string' 
                                    ? report.template_id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) 
                                    : report.template_name || report.name || 'Report'
                                }
                            </h6>
                            <small className="text-muted">ID: {report.id}</small>
                        </div>
                        <div className="col-md-2">
                            <Badge bg={statusConfig.variant} className="d-flex align-items-center gap-1">
                                <StatusIcon size={12} />
                                {statusConfig.text}
                            </Badge>
                            {report.status === 'running' && (
                                <ProgressBar 
                                    now={report.progress_percentage || 0} 
                                    size="sm" 
                                    className="mt-1"
                                    style={{ height: '4px' }}
                                />
                            )}
                        </div>
                        <div className="col-md-2">
                            <div className="text-muted small">
                                <div>
                                    {getFormatIcon(report.export_format)} 
                                    {typeof report.export_format === 'string' ? report.export_format.toUpperCase() : 'PDF'}
                                </div>
                                <div>{formatFileSize(report.file_size_bytes)}</div>
                            </div>
                        </div>
                        <div className="col-md-2">
                            <div className="text-muted small">
                                {formatDate(report.created_at)}
                            </div>
                        </div>
                        <div className="col-md-1">
                            <Dropdown>
                                <Dropdown.Toggle variant="link" size="sm" className="text-muted">
                                    <BsThreeDotsVertical />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={handleView}>
                                        <BsEye className="me-2" />
                                        View Details
                                    </Dropdown.Item>
                                    {report.status === 'completed' && (
                                        <Dropdown.Item onClick={handleDownload} disabled={isLoading}>
                                            <BsDownload className="me-2" />
                                            {isLoading ? 'Downloading...' : 'Download'}
                                        </Dropdown.Item>
                                    )}
                                    {(report.status === 'running' || report.status === 'pending') && (
                                        <Dropdown.Item onClick={handleCancel} disabled={isLoading}>
                                            <BsStopCircle className="me-2" />
                                            Cancel
                                        </Dropdown.Item>
                                    )}
                                    <Dropdown.Divider />
                                    <Dropdown.Item onClick={handleDelete} className="text-danger">
                                        <BsTrash className="me-2" />
                                        Delete
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    // Grid view (default)
    return (
        <Card className={`report-card border-0 shadow-sm h-100 ${isSelected ? 'border-primary' : ''}`}>
            <Card.Header className="bg-transparent border-0 pb-0">
                <div className="d-flex justify-content-between align-items-start">
                    <div className="report-type">
                        <span className="badge bg-light text-dark small">
                            {getFormatIcon(report.export_format)} 
                            {typeof report.export_format === 'string' ? report.export_format.toUpperCase() : 'PDF'}
                        </span>
                    </div>
                    {onSelect && (
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => onSelect(report.id, e.target.checked)}
                            className="form-check-input"
                        />
                    )}
                </div>
            </Card.Header>
            <Card.Body className="pt-2">
                <div className="text-center mb-3">
                    <BsFileText className="display-6 text-primary mb-2" />
                    <h6 className="fw-semibold mb-1">
                        {typeof report.template_id === 'string' 
                            ? report.template_id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) 
                            : report.template_name || report.name || 'Report'
                        }
                    </h6>
                    <small className="text-muted">ID: {report.id.substring(0, 8)}...</small>
                </div>

                <div className="mb-3">
                    <Badge bg={statusConfig.variant} className="d-flex align-items-center justify-content-center gap-1 w-100 py-2">
                        <StatusIcon size={14} />
                        {statusConfig.text}
                    </Badge>
                    {report.status === 'running' && (
                        <ProgressBar 
                            now={report.progress_percentage || 0} 
                            className="mt-2"
                            style={{ height: '6px' }}
                            label={`${report.progress_percentage || 0}%`}
                        />
                    )}
                </div>

                <div className="report-details small text-muted mb-3">
                    <div className="d-flex justify-content-between">
                        <span>Records:</span>
                        <span>{report.record_count || 'N/A'}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                        <span>Size:</span>
                        <span>{formatFileSize(report.file_size_bytes)}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                        <span>Created:</span>
                        <span>{formatDate(report.created_at)}</span>
                    </div>
                    {report.download_count > 0 && (
                        <div className="d-flex justify-content-between">
                            <span>Downloads:</span>
                            <span>{report.download_count}</span>
                        </div>
                    )}
                </div>

                {report.error_message && (
                    <div className="alert alert-danger small py-2 mb-3">
                        {report.error_message}
                    </div>
                )}
            </Card.Body>
            <Card.Footer className="bg-transparent border-0 pt-0">
                <div className="d-grid gap-2">
                    <Button variant="outline-primary" size="sm" onClick={handleView}>
                        <BsEye className="me-1" />
                        View Details
                    </Button>
                    {report.status === 'completed' && (
                        <Button 
                            variant="primary" 
                            size="sm" 
                            onClick={handleDownload}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-1" />
                                    Downloading...
                                </>
                            ) : (
                                <>
                                    <BsDownload className="me-1" />
                                    Download
                                </>
                            )}
                        </Button>
                    )}
                    {(report.status === 'running' || report.status === 'pending') && (
                        <Button 
                            variant="outline-warning" 
                            size="sm" 
                            onClick={handleCancel}
                            disabled={isLoading}
                        >
                            <BsStopCircle className="me-1" />
                            Cancel
                        </Button>
                    )}
                </div>
            </Card.Footer>
        </Card>
    );
};

ReportCard.propTypes = {
    report: PropTypes.shape({
        id: PropTypes.string.isRequired,
        template_id: PropTypes.string.isRequired,
        status: PropTypes.oneOf(['pending', 'running', 'completed', 'failed', 'cancelled']).isRequired,
        created_at: PropTypes.string.isRequired,
        started_at: PropTypes.string,
        completed_at: PropTypes.string,
        user_id: PropTypes.string.isRequired,
        export_format: PropTypes.string.isRequired,
        progress_percentage: PropTypes.number,
        record_count: PropTypes.number,
        error_message: PropTypes.string,
        file_size_bytes: PropTypes.number,
        download_count: PropTypes.number
    }).isRequired,
    onView: PropTypes.func,
    onDownload: PropTypes.func,
    onDelete: PropTypes.func,
    isSelected: PropTypes.bool,
    onSelect: PropTypes.func,
    viewMode: PropTypes.oneOf(['grid', 'list'])
};

export default ReportCard;